import { decodeHtmlEntities } from './messages';

/** CMS content kinds, ported from `Enums.PageTypes`. */
export const PageType = {
  Page: 0,
  Banner: 1,
  Popup: 2,
  Bonus: 3,
  LandingPage: 4,
  SocialMedia: 5,
  Buttons: 6,
} as const;

export type PageType = (typeof PageType)[keyof typeof PageType];

/**
 * Banner slots. The numbering has gaps and is out of order because positions were
 * added over time; it matches the CMS exactly.
 */
export const BannerPosition = {
  HomePage: 0,
  RightSide: 1,
  Desktop: 2,
  Mobile: 3,
  Casino: 4,
  CasinoMobile: 5,
  LiveCasinoMobileHome: 6,
  TopBannerMobile: 7,
  ProvidersMobile: 8,
  BackgroundHomepageDesktop: 9,
  PaymentProvidersMobile: 10,
  LiveCasino: 11,
  LiveCasinoMobile: 12,
  BackgroundHomepageMobile: 13,
} as const;

export type BannerPosition = (typeof BannerPosition)[keyof typeof BannerPosition];

/** Footer/menu grouping for content pages, from `Enums.PageSection`. */
export const PageSection = {
  About: 100,
  User: 101,
  SportsBetting: 102,
  Casino: 103,
  Poker: 104,
  Affiliate: 105,
  ResponsibleGambling: 106,
  TopInfo: 111,
} as const;

export type PageSection = (typeof PageSection)[keyof typeof PageSection];

/**
 * CMS popup device targeting — SportsAdmin `ddlPopupType`:
 * `0` Desktop Everywhere, `1` Mobile Everywhere.
 * (Replaced the older All Users / Not Registered / … options.)
 */
export const PopupType = {
  DesktopEverywhere: 0,
  MobileEverywhere: 1,
  /** @deprecated Prefer `DesktopEverywhere` — same value `0`. */
  Desktop: 0,
  /** @deprecated Prefer `MobileEverywhere` — same value `1`. */
  Mobile: 1,
} as const;

export type PopupType = (typeof PopupType)[keyof typeof PopupType];

/** CMS-configured buttons placed in the shell, from `Enums.ButtonTypes`. */
export const ButtonType = {
  LiveTVDesktop: 300,
  LiveTVMobile: 301,
  ExternalSportsbookDesktop: 302,
  ExternalSportsbookMobile: 303,
  AviatorDesktop: 304,
  AviatorMobile: 305,
  LeftHeaderMobile: 306,
  LeftHeaderDesktop: 307,
  RightHeaderMobile: 308,
  RightHeaderDesktop: 309,
} as const;

export type ButtonType = (typeof ButtonType)[keyof typeof ButtonType];

export const ContactFormType = {
  Contact: 0,
  Partner: 1,
} as const;

export type ContactFormType = (typeof ContactFormType)[keyof typeof ContactFormType];

export const MessageStatus = {
  New: 'New',
  Read: 'Read',
} as const;

export function messageStatusOf(isRead: boolean): string {
  return isRead ? MessageStatus.Read : MessageStatus.New;
}

export interface CmsPage {
  Id: number | string;
  Type: number;
  /** Banner/footer slot; promotions use `null` (legacy `Position === null`). */
  Position: number | null;
  /**
   * Device targeting for popup pages — SportsAdmin `ddlPopupType`
   * (`0` Desktop Everywhere / `1` Mobile Everywhere). Omitted on some API
   * projections of the general pages list.
   */
  PopupType?: number | null;
  DisplayIndex: number;
  Image: string | null;
  Text?: string;
  Title?: string;
  Url?: string;
  LinkUrl?: string;
}

/** View model for the public `/promotions` list. */
export interface Promotion {
  id: string | number;
  slug: string;
  title: string;
  imageUrl: string;
  description?: string;
  displayIndex: number;
}

export interface CmsPagesListResponse {
  Data: CmsPage[];
}

export interface Banner {
  id: string | number;
  imageUrl: string;
  linkUrl?: string;
  htmlText?: string;
  displayIndex: number;
}

/** View model for the bootstrap CMS promo popup (legacy `CheckForPopups`). */
export interface PromoPopup {
  id: string | number;
  imageUrl: string;
  linkUrl?: string;
}

/** View model for the site-wide top news ticker (legacy `TopInfoTicker`). */
export interface TopInfoTickerContent {
  id: string | number;
  htmlText: string;
}

export type BannerLink =
  | { kind: 'none' }
  | { kind: 'external'; href: string }
  | { kind: 'internal'; href: string };

export function cmsPageImageUrl(
  usersBaseUrl: string,
  accountGuid: string,
  pageId: string | number,
  filename: string,
): string {
  const base = usersBaseUrl.replace(/\/+$/, '');
  return `${base}/DataFiles/${accountGuid}/Pages/${pageId}/Main/${filename}`;
}

/**
 * Decode percent-runs (`%3C`, `%C4%B1`) without treating literal `%10` (10%) as a byte.
 * Leaves lone `%XX` that decode only to C0 controls (except tab/LF/CR) unchanged.
 */
function decodeCmsPercentEscapes(value: string): string {
  return value.replace(/(?:%[0-9A-Fa-f]{2})+/g, (seq) => {
    try {
      const decoded = decodeURIComponent(seq);
      const onlyControls = [...decoded].every((ch) => {
        const code = ch.charCodeAt(0);
        return code < 0x20 && code !== 9 && code !== 10 && code !== 13;
      });
      return onlyControls ? seq : decoded;
    } catch {
      return seq;
    }
  });
}

/**
 * Decode CMS `Text` only (not titles) — `%uXXXX`, safe `%XX` / UTF-8, then HTML
 * entities (`&Ouml;`, `&uuml;`, …). Used for bodies, banners, and plain excerpts.
 */
export function unescapeCmsText(value: string | undefined | null): string | undefined {
  if (value == null || value === '') return undefined;

  // Legacy `unescape`: `%u0131` → ı, `%u015F` → ş, etc.
  let decoded = value.replace(/%u([0-9A-Fa-f]{4})/gi, (_, hex: string) =>
    String.fromCharCode(Number.parseInt(hex, 16)),
  );

  decoded = decodeCmsPercentEscapes(decoded);
  decoded = decodeHtmlEntities(decoded);
  return decoded === '' ? undefined : decoded;
}

export function resolveBannerLink(linkUrl: string | undefined | null): BannerLink {
  const href = linkUrl?.trim();
  if (!href) return { kind: 'none' };
  if (href.includes('http')) return { kind: 'external', href };
  return { kind: 'internal', href: href.startsWith('/') ? href : `/${href}` };
}

/**
 * Promo popup from the shared CMS pages list — `Type === Popup` and
 * `PopupType` matching SportsAdmin Desktop/Mobile Everywhere, sorted by
 * `DisplayIndex`. Rows without `PopupType` are ignored (never shown on the
 * wrong viewport).
 */
export function promoPopupFromPages(
  pages: readonly CmsPage[] | null | undefined,
  popupType: PopupType,
  usersBaseUrl: string,
  accountGuid: string,
): PromoPopup | null {
  if (!pages?.length || !accountGuid) return null;

  const page = pages
    .filter(
      (item): item is CmsPage & { Image: string } =>
        item.Type === PageType.Popup &&
        Boolean(item.Image?.trim()) &&
        Number(item.PopupType) === popupType,
    )
    .slice()
    .sort((a, b) => (a.DisplayIndex ?? 0) - (b.DisplayIndex ?? 0))[0];

  if (!page) return null;

  return {
    id: page.Id,
    imageUrl: cmsPageImageUrl(usersBaseUrl, accountGuid, page.Id, page.Image.trim()),
    ...(page.LinkUrl?.trim() ? { linkUrl: page.LinkUrl.trim() } : {}),
  };
}

export function bannersFromPages(
  pages: readonly CmsPage[],
  position: BannerPosition,
  usersBaseUrl: string,
  accountGuid: string,
): Banner[] {
  return pages
    .filter(
      (page): page is CmsPage & { Image: string } =>
        page.Type === PageType.Banner && page.Position === position && Boolean(page.Image),
    )
    .slice()
    .sort((a, b) => (a.DisplayIndex ?? 0) - (b.DisplayIndex ?? 0))
    .map((page) => {
      const htmlText = unescapeCmsText(page.Text);
      return {
        id: page.Id,
        imageUrl: cmsPageImageUrl(usersBaseUrl, accountGuid, page.Id, page.Image),
        ...(page.LinkUrl ? { linkUrl: page.LinkUrl } : {}),
        ...(htmlText ? { htmlText } : {}),
        displayIndex: page.DisplayIndex ?? 0,
      };
    });
}

/** Strip legacy CMS `&nbsp;` (literal or decoded) from ticker HTML. */
function stripCmsNbsp(html: string): string {
  return html.replace(/&nbsp;/gi, '').replace(/\u00A0/g, '');
}

/**
 * First TopInfo CMS page for the site-wide news ticker (legacy `TopInfoTicker`).
 * Matches `LoadList(Page, TopInfo)` then `Data[0]` after `DisplayIndex` sort.
 */
export function topInfoTickerFromPages(
  pages: readonly CmsPage[] | null | undefined,
): TopInfoTickerContent | null {
  if (!pages?.length) return null;

  const matches = pages
    .filter(
      (page) =>
        Number(page.Type) === PageType.Page && Number(page.Position) === PageSection.TopInfo,
    )
    .slice()
    .sort((a, b) => (a.DisplayIndex ?? 0) - (b.DisplayIndex ?? 0));

  for (const page of matches) {
    const decoded = unescapeCmsText(page.Text);
    if (!decoded) continue;
    const htmlText = stripCmsNbsp(decoded).trim();
    if (!htmlText) continue;
    return { id: page.Id, htmlText };
  }

  return null;
}

const HTML_TAG_RE = /<[^>]+>/g;
const WHITESPACE_RE = /\s+/g;

/** Plain-text excerpt from CMS HTML for promo cards / hero subtitles. */
export function cmsPlainTextExcerpt(
  value: string | undefined,
  maxLength = 160,
): string | undefined {
  const html = unescapeCmsText(value);
  if (!html) return undefined;

  const plain = html
    .replace(HTML_TAG_RE, ' ')
    .replace(WHITESPACE_RE, ' ')
    .trim();
  if (!plain) return undefined;

  if (plain.length <= maxLength) return plain;
  const clipped = plain.slice(0, maxLength).replace(/\s+\S*$/, '').trim();
  return clipped ? `${clipped}…` : `${plain.slice(0, maxLength)}…`;
}

/**
 * Bonus CMS pages for the Promotions list — `Type === Bonus` and `Position == null`,
 * sorted by `DisplayIndex` (legacy `LoadList(Enums.PageTypes.Bonus)`).
 */
export function promotionsFromPages(
  pages: readonly CmsPage[],
  usersBaseUrl: string,
  accountGuid: string,
): Promotion[] {
  return pages
    .filter(
      (page): page is CmsPage & { Image: string; Url: string } =>
        page.Type === PageType.Bonus &&
        page.Position == null &&
        Boolean(page.Url?.trim()) &&
        Boolean(page.Image?.trim()),
    )
    .slice()
    .sort((a, b) => (a.DisplayIndex ?? 0) - (b.DisplayIndex ?? 0))
    .map((page) => {
      const title = page.Title?.trim() || page.Url.trim();
      const description = cmsPlainTextExcerpt(page.Text);
      return {
        id: page.Id,
        slug: page.Url.trim(),
        title,
        imageUrl: cmsPageImageUrl(usersBaseUrl, accountGuid, page.Id, page.Image),
        ...(description ? { description } : {}),
        displayIndex: page.DisplayIndex ?? 0,
      };
    });
}

export interface AccountLogoSource {
  Guid?: string;
  AccountDomainId?: string | number;
  DomainImages?: {
    Logo?: string | null;
  } | null;
}

export function accountLogoUrl(
  usersBaseUrl: string,
  account: AccountLogoSource | null | undefined,
): string | null {
  const accountGuid = typeof account?.Guid === 'string' ? account.Guid.trim() : '';
  const accountDomainId =
    account?.AccountDomainId === undefined || account?.AccountDomainId === null
      ? ''
      : String(account.AccountDomainId).trim();
  const logoFile = account?.DomainImages?.Logo?.trim() ?? '';

  if (!accountGuid || !accountDomainId || !logoFile) return null;
  const base = usersBaseUrl.replace(/\/+$/, '');
  return `${base}/DataFiles/${accountGuid}/Logo/${accountDomainId}/${logoFile}`;
}

export interface AccountShellVisibilitySource {
  ShowLogo?: boolean;
  ShowFooter?: boolean;
}

export interface ShellVisibility {
  showLogo: boolean;
  showFooter: boolean;
}

export function shellVisibilityFromAccount(
  account: AccountShellVisibilitySource | null | undefined,
): ShellVisibility {
  return {
    // Header is always shown in the React shell; only logo/footer follow account flags.
    showLogo: account?.ShowLogo !== false,
    showFooter: account?.ShowFooter !== false,
  };
}

export interface FooterCmsLink {
  id: string;
  label: string;
  /** Legacy WebsiteTranslates key; when set, UI should look this up via `t()`. */
  translateKey?: string;
  href: string;
  external: boolean;
  order: number;
}

export type FooterSectionKey =
  | 'about'
  | 'user'
  | 'sportsBetting'
  | 'casino'
  | 'poker'
  | 'affiliatePartner'
  | 'responsibleGambling';

export interface FooterCmsSection {
  key: FooterSectionKey;
  title: string;
  /** Legacy WebsiteTranslates key for the section heading. */
  translateKey?: string;
  position: PageSection | null;
  links: FooterCmsLink[];
}

export interface FooterFixedLinkOptions {
  allowUserRegistration?: boolean;
  allowDeposits?: boolean;
}

function fixedLink(
  id: string,
  label: string,
  translateKey: string,
  href: string,
  order: number,
): FooterCmsLink {
  return { id, label, translateKey, href, external: false, order };
}

/**
 * Hard-coded footer links from legacy `footerlinks` template.
 * Order is negative so fixed links appear before CMS entries (`DisplayIndex` >= 0).
 */
function fixedFooterLinks(options: FooterFixedLinkOptions = {}): Record<FooterSectionKey, FooterCmsLink[]> {
  const allowRegister = options.allowUserRegistration !== false;
  const allowDeposits = options.allowDeposits !== false;

  const userLinks: FooterCmsLink[] = [];
  if (allowRegister) {
    userLinks.push(fixedLink('fixed-register', 'Register', 'Register', '/register', -40));
  }
  userLinks.push(fixedLink('fixed-login', 'Login', 'Login', '/login', -30));
  if (allowDeposits) {
    userLinks.push(
      fixedLink('fixed-deposit', 'Deposit', 'Deposit', '/profile/deposits', -20),
    );
  }
  if (allowRegister) {
    userLinks.push(
      fixedLink(
        'fixed-forgot-password',
        'Forgot Password?',
        'ForgotPassword',
        '/forgotpassword',
        -10,
      ),
    );
  }

  return {
    about: [],
    user: userLinks,
    sportsBetting: [
      fixedLink('fixed-sports', 'Sports Betting', 'SportBetting', '/sports', -50),
      fixedLink('fixed-live', 'Live Betting', 'LiveBetting', '/live', -40),
      fixedLink('fixed-football', 'Football', 'Football', '/sports/football', -30),
      fixedLink('fixed-basketball', 'Basketball', 'Basketball', '/sports/basketball', -20),
      fixedLink('fixed-tennis', 'Tennis', 'Tennis', '/sports/tennis', -10),
    ],
    casino: [
      fixedLink('fixed-roulette', 'Roulette', 'Roulette', '/casino', -40),
      fixedLink('fixed-blackjack', 'BlackJack', 'BlackJack', '/casino', -30),
      fixedLink('fixed-baccarat', 'Baccarat', 'Baccarat', '/casino', -20),
      fixedLink('fixed-slots', 'Slots', 'Slots', '/casino', -10),
    ],
    poker: [],
    affiliatePartner: [fixedLink('fixed-partner', 'Partner', 'Partner', '/partner', -10)],
    responsibleGambling: [],
  };
}

/** Bottom policy links from the legacy footer row under Responsible Gambling. */
export const FOOTER_POLICY_LINKS: FooterCmsLink[] = [
  {
    id: 'fixed-aml',
    label: 'Anti Money Laundering Policy',
    translateKey: 'AntiMoneyLaunderingPolicy',
    href: '/page/Anti-Money-Laundering-Policy',
    external: false,
    order: 0,
  },
  {
    id: 'fixed-kyc',
    label: 'Know Your Customer Policy',
    translateKey: 'KnowYourCustomerPolicy',
    href: '/page/Know-Your-Customer-Policy',
    external: false,
    order: 1,
  },
];

const FOOTER_SECTION_ORDER: Array<Omit<FooterCmsSection, 'links'>> = [
  { key: 'about', title: 'About', translateKey: 'About', position: PageSection.About },
  { key: 'user', title: 'User', translateKey: 'User', position: PageSection.User },
  {
    key: 'sportsBetting',
    title: 'Sports Betting',
    translateKey: 'SportsBetting',
    position: PageSection.SportsBetting,
  },
  { key: 'casino', title: 'Casino', translateKey: 'Casino', position: PageSection.Casino },
  { key: 'poker', title: 'Poker', translateKey: 'Poker', position: PageSection.Poker },
  {
    key: 'affiliatePartner',
    title: 'Affiliate Partner',
    translateKey: 'AffiliatePartner',
    position: PageSection.Affiliate,
  },
  {
    key: 'responsibleGambling',
    title: 'Responsible Gambling',
    translateKey: 'ResponsibleGambling',
    position: PageSection.ResponsibleGambling,
  },
];

/** Human-readable CMS page section title from `Page.Position` (footer section names). */
export function pageSectionLabel(position: number | null | undefined): string | undefined {
  if (position == null) return undefined;
  return FOOTER_SECTION_ORDER.find((section) => section.position === position)?.title;
}

/** WebsiteTranslates key for a CMS page section, when `Position` maps to a footer section. */
export function pageSectionTranslateKey(
  position: number | null | undefined,
): string | undefined {
  if (position == null) return undefined;
  return FOOTER_SECTION_ORDER.find((section) => section.position === position)?.translateKey;
}

function asFooterLink(page: CmsPage): FooterCmsLink | null {
  const title =
    page.Title?.trim() || cmsPlainTextExcerpt(page.Text) || page.Url?.trim() || '';
  const linkUrl = page.LinkUrl?.trim() ?? '';
  if (!title) return null;

  if (linkUrl) {
    return {
      id: String(page.Id),
      label: title,
      translateKey: title,
      href: linkUrl,
      external: true,
      order: page.DisplayIndex ?? 0,
    };
  }

  const urlSlug = page.Url?.trim();
  if (!urlSlug) return null;
  return {
    id: String(page.Id),
    label: title,
    translateKey: title,
    href: `/page/${urlSlug}`,
    external: false,
    order: page.DisplayIndex ?? 0,
  };
}

export function footerSectionsFromPages(
  pages: readonly CmsPage[],
  options: FooterFixedLinkOptions = {},
): FooterCmsSection[] {
  const fixedBySection = fixedFooterLinks(options);
  const pageSections = FOOTER_SECTION_ORDER.map((section) => ({
    ...section,
    links: [...fixedBySection[section.key]],
  }));
  const byPosition = new Map<number, FooterCmsSection>(
    pageSections
      .filter((section) => section.position != null)
      .map((section) => [section.position as number, section]),
  );

  for (const page of pages) {
    if (page.Type !== PageType.Page || page.Position == null) continue;

    const section = byPosition.get(page.Position);
    if (!section) continue;

    const link = asFooterLink(page);
    if (!link) continue;
    section.links.push(link);
  }

  for (const section of pageSections) {
    section.links.sort((a, b) => a.order - b.order);
  }

  // Keep sections that have either fixed or CMS links (matches legacy always-rendered columns).
  return pageSections.filter((section) => section.links.length > 0);
}

export interface FooterSocialLink {
  id: string;
  href: string;
  imageUrl: string;
  label: string;
  order: number;
}

/** Normalize CMS social `LinkUrl` the same way as legacy (`https://` + stripped scheme). */
export function socialMediaHref(linkUrl: string | undefined | null): string | null {
  const raw = linkUrl?.trim();
  if (!raw) return null;
  return `https://${raw.replace(/^https?:\/\//i, '')}`;
}

/**
 * Social icons for the footer — `PageType.SocialMedia`, sorted by `DisplayIndex`.
 * Matches legacy `SportsApp.Modules.CMS.SocialMedia` / `SocialMediaFunctionality`.
 */
export function socialMediaFromPages(
  pages: readonly CmsPage[],
  usersBaseUrl: string,
  accountGuid: string,
): FooterSocialLink[] {
  return pages
    .filter(
      (page): page is CmsPage & { Image: string } =>
        page.Type === PageType.SocialMedia && Boolean(page.Image?.trim()),
    )
    .slice()
    .sort((a, b) => (a.DisplayIndex ?? 0) - (b.DisplayIndex ?? 0))
    .flatMap((page) => {
      const href = socialMediaHref(page.LinkUrl);
      if (!href) return [];
      return [
        {
          id: String(page.Id),
          href,
          imageUrl: cmsPageImageUrl(usersBaseUrl, accountGuid, page.Id, page.Image),
          label: page.Title?.trim() || cmsPlainTextExcerpt(page.Text) || 'Social media',
          order: page.DisplayIndex ?? 0,
        },
      ];
    });
}
